/**
 * HTML Sanitization utility
 * Uses DOMPurify for client-side, basic regex sanitization for server-side
 */

// DOMPurify configuration - allow safe HTML tags only
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'b',
    'i',
    'em',
    'strong',
    'a',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'pre',
    'code',
    'span',
    'div',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'img',
    'figure',
    'figcaption',
    'sup',
    'sub',
    'hr',
  ],
  ALLOWED_ATTR: [
    'href',
    'src',
    'alt',
    'title',
    'class',
    'id',
    'target',
    'rel',
    'width',
    'height',
    'colspan',
    'rowspan',
  ],
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target'],
  FORBID_TAGS: [
    'script',
    'style',
    'iframe',
    'form',
    'input',
    'button',
    'object',
    'embed',
  ],
  FORBID_ATTR: [
    'onerror',
    'onclick',
    'onload',
    'onmouseover',
    'onfocus',
    'onblur',
  ],
};

/**
 * Basic sanitization using regex (works on both client and server)
 * Removes dangerous patterns without requiring DOM
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
function basicSanitize(html) {
  if (!html || typeof html !== 'string') return html;

  let sanitized = html;

  // Remove script tags and content
  sanitized = sanitized.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    ''
  );

  // Remove event handlers (onclick, onerror, onload, etc.)
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*[^\s>]*/gi, '');

  // Remove javascript: URLs
  sanitized = sanitized.replace(
    /href\s*=\s*["']?\s*javascript:[^"'>]*/gi,
    'href="#"'
  );
  sanitized = sanitized.replace(
    /src\s*=\s*["']?\s*javascript:[^"'>]*/gi,
    'src=""'
  );

  // Remove data: URLs in src (can be used for XSS)
  sanitized = sanitized.replace(
    /src\s*=\s*["']?\s*data:text\/html[^"'>]*/gi,
    'src=""'
  );

  // Remove style tags and content
  sanitized = sanitized.replace(
    /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi,
    ''
  );

  // Remove iframe, object, embed, form tags
  sanitized = sanitized.replace(
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    ''
  );
  sanitized = sanitized.replace(
    /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
    ''
  );
  sanitized = sanitized.replace(/<embed[^>]*\/?>/gi, '');
  sanitized = sanitized.replace(
    /<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi,
    ''
  );

  // Remove base tags (can redirect all relative URLs)
  sanitized = sanitized.replace(/<base[^>]*\/?>/gi, '');

  // Remove meta refresh
  sanitized = sanitized.replace(
    /<meta[^>]*http-equiv\s*=\s*["']?refresh[^>]*>/gi,
    ''
  );

  return sanitized;
}

/**
 * Sanitize HTML content to prevent XSS attacks
 * Uses consistent sanitization on both server and client to prevent hydration mismatch
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export function sanitizeHTML(html) {
  if (!html || typeof html !== 'string') return html;

  // Use basic sanitization consistently on both server and client
  // This prevents hydration mismatch since output is identical
  return basicSanitize(html);
}

export { basicSanitize };
export default sanitizeHTML;
